import React, { useState } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import LocationAutocomplete from './LocationAutocomplete';

function SynastryOracle({ session }) {
  const [formData, setFormData] = useState({
    personA_name: '', personA_year: 1990, personA_month: 1, personA_day: 1, personA_time: "12:00", personA_lat: 37.77, personA_lon: -122.41, personA_utcOffset: 0,
    personB_name: '', personB_year: 1990, personB_month: 1, personB_day: 1, personB_time: "12:00", personB_lat: 37.77, personB_lon: -122.41, personB_utcOffset: 0,
  });
  
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  if (session?.user?.email !== 'eduardoadonis@gmail.com') {
    return (
      <div style={{ textAlign: 'center', padding: '100px 20px', color: '#ffca28', minHeight: '60vh' }}>
        <h2 style={{ textTransform: 'uppercase', letterSpacing: '2px' }}>Vault Locked</h2>
        <p style={{ color: '#8b949e', marginTop: '15px' }}>This terminal is restricted to the Lead Architect.</p>
      </div>
    );
  }

  // Helper function to calculate exact UTC time (mirrors App.js logic)
  const calculateUtcHour = (timeStr, offset) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const localDecimalHour = hours + (minutes / 60);
    return localDecimalHour - offset;
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setReport(null);
    
    try {
      // 1. Fetch Subject Alpha's Chart from your existing engine
      const alphaUtc = calculateUtcHour(formData.personA_time, formData.personA_utcOffset);
      const alphaRes = await axios.get('/api/calculate', { params: {
        year: formData.personA_year, month: formData.personA_month, day: formData.personA_day,
        hour_utc: alphaUtc, lat: formData.personA_lat, lon_geo: formData.personA_lon
      }});

      // 2. Fetch Subject Omega's Chart from your existing engine
      const omegaUtc = calculateUtcHour(formData.personB_time, formData.personB_utcOffset);
      const omegaRes = await axios.get('/api/calculate', { params: {
        year: formData.personB_year, month: formData.personB_month, day: formData.personB_day,
        hour_utc: omegaUtc, lat: formData.personB_lat, lon_geo: formData.personB_lon
      }});

      // 3. Send BOTH fully completed charts to the Synastry Python script
      const synastryPayload = {
        personA_name: formData.personA_name || "Subject Alpha",
        personB_name: formData.personB_name || "Subject Omega",
        personA_chart: alphaRes.data,
        personB_chart: omegaRes.data
      };

      const response = await axios.post('/api/synastry', synastryPayload);
      setReport(response.data);

    } catch (err) {
      console.error("Oracle Engine misaligned:", err);
      // This will now show you EXACTLY what failed on the screen
      const errorMsg = err.response?.data?.error || err.message;
      alert(`Matrix glitch: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const renderInput = (label, field, type="text") => (
    <div style={{ marginBottom: '15px' }}>
      <label style={{ display: 'block', color: '#8b949e', fontSize: '0.85rem', marginBottom: '5px' }}>{label}</label>
      <input 
        type={type} 
        value={formData[field]} 
        onChange={(e) => setFormData({...formData, [field]: e.target.value})}
        style={{ width: '100%', padding: '10px', background: '#05070a', border: '1px solid #30363d', color: 'white', borderRadius: '4px', boxSizing: 'border-box', colorScheme: 'dark' }}
      />
    </div>
  );

  return (
    <div style={{ maxWidth: '1000px', margin: '40px auto', padding: '0 20px' }}>
      <Helmet><title>Synastry Oracle Engine | Adonisian Matrix</title></Helmet>

      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <h2 style={{ color: '#ffca28', fontSize: '2rem', letterSpacing: '2px', textTransform: 'uppercase', margin: '0 0 10px 0' }}>👁️ Synastry Oracle Engine</h2>
        <p style={{ color: '#00d4ff', fontSize: '1rem', fontStyle: 'italic', margin: 0 }}>Master-Builder Terminal: Live Compilation & Vault Dispatch</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '30px', marginBottom: '30px' }}>
        
        {/* PERSON A COLUMN */}
        <div style={{ background: 'rgba(0, 212, 255, 0.03)', border: '1px solid #00d4ff', padding: '25px', borderRadius: '8px' }}>
          <h3 style={{ color: '#00d4ff', marginTop: 0, borderBottom: '1px solid #30363d', paddingBottom: '10px' }}>Subject Alpha</h3>
          {renderInput("Name", "personA_name")}
          <div style={{ display: 'flex', gap: '10px' }}>
            {renderInput("Year", "personA_year", "number")}
            {renderInput("Month", "personA_month", "number")}
            {renderInput("Day", "personA_day", "number")}
          </div>
          {renderInput("Exact Local Time", "personA_time", "time")}
          
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', color: '#8b949e', fontSize: '0.85rem', marginBottom: '5px' }}>Birth Location</label>
            <LocationAutocomplete 
              onLocationCalculated={(loc) => setFormData(prev => ({
                ...prev, personA_lat: loc.lat, personA_lon: loc.lng, personA_utcOffset: loc.utcOffset 
              }))} 
            />
          </div>
        </div>

        {/* PERSON B COLUMN */}
        <div style={{ background: 'rgba(255, 202, 40, 0.03)', border: '1px solid #ffca28', padding: '25px', borderRadius: '8px' }}>
          <h3 style={{ color: '#ffca28', marginTop: 0, borderBottom: '1px solid #30363d', paddingBottom: '10px' }}>Subject Omega</h3>
          {renderInput("Name", "personB_name")}
          <div style={{ display: 'flex', gap: '10px' }}>
            {renderInput("Year", "personB_year", "number")}
            {renderInput("Month", "personB_month", "number")}
            {renderInput("Day", "personB_day", "number")}
          </div>
          {renderInput("Exact Local Time", "personB_time", "time")}
          
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', color: '#8b949e', fontSize: '0.85rem', marginBottom: '5px' }}>Birth Location</label>
            <LocationAutocomplete 
              onLocationCalculated={(loc) => setFormData(prev => ({
                ...prev, personB_lat: loc.lat, personB_lon: loc.lng, personB_utcOffset: loc.utcOffset 
              }))} 
            />
          </div>
        </div>
      </div>

      <div style={{ textAlign: 'center' }}>
        <button 
          onClick={handleGenerate} disabled={loading}
          style={{ backgroundColor: loading ? '#30363d' : '#ffca28', color: loading ? '#8b949e' : '#0d1117', border: 'none', padding: '15px 40px', fontSize: '1.2rem', fontWeight: 'bold', textTransform: 'uppercase', borderRadius: '4px', cursor: loading ? 'not-allowed' : 'pointer' }}
        >
          {loading ? "⚡ Compiling Matrix & Dispatching Email..." : "Generate Astro-Somatic Blueprint"}
        </button>
      </div>

      {report && (
        <div style={{ marginTop: '50px', background: '#05070a', border: '1px dashed #ffca28', padding: '30px', borderRadius: '8px' }}>
          <h3 style={{ color: '#ffffff', letterSpacing: '1px', borderBottom: '1px solid #30363d', paddingBottom: '10px' }}>✅ Matrix Compiled: {formData.personA_name} & {formData.personB_name}</h3>
          <p style={{ color: '#00d4ff', fontStyle: 'italic', fontSize: '0.9rem' }}>A comprehensive PDF blueprint has been successfully dispatched to eduardoadonis@gmail.com.</p>
          <h4 style={{ color: '#ffca28', marginTop: '20px' }}>Live Key Intersections:</h4>
          <ul style={{ color: '#c9d1d9', lineHeight: '1.8' }}>
            {report.aspects.map((aspect, i) => <li key={i}>{aspect}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

export default SynastryOracle;